import { useState, useEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';

interface DateInputFieldProps {
    value: string; // ISO YYYY-MM-DD stored value
    onChange: (isoDate: string) => void;
    error?: boolean;
    helperText?: string;
    required?: boolean;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    placeholder?: string;
    min?: string; // ISO YYYY-MM-DD min for calendar picker
    max?: string; // ISO YYYY-MM-DD max for calendar picker
    label?: string;
    InputLabelProps?: any;
    sx?: any;
}

export const DateInputField = ({
    value,
    onChange,
    error,
    helperText,
    required,
    size = 'small',
    fullWidth,
    placeholder = 'DD/MM/YYYY',
    min,
    max,
    label,
    InputLabelProps,
    sx,
}: DateInputFieldProps) => {
    const [dateInput, setDateInput] = useState('');
    const pickerRef = useRef<HTMLInputElement>(null);

    // Sync ISO value → DD/MM/YYYY display
    useEffect(() => {
        if (!value) { setDateInput(''); return; }
        const m = moment(value, 'YYYY-MM-DD', true);
        if (m.isValid()) setDateInput(m.format('DD/MM/YYYY'));
    }, [value]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
        let display = digits;
        if (digits.length > 4) display = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        else if (digits.length > 2) display = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        setDateInput(display);
        if (digits.length === 8) {
            const parsed = moment(digits, 'DDMMYYYY', true);
            if (parsed.isValid()) onChange(parsed.format('YYYY-MM-DD'));
        } else if (digits.length === 0) {
            onChange('');
        }
    };

    return (
        <TextField
            size={size}
            value={dateInput}
            onChange={handleTextChange}
            placeholder={placeholder}
            label={label}
            required={required}
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
            InputLabelProps={InputLabelProps}
            sx={sx}
            inputProps={{ maxLength: 10 }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => pickerRef.current?.showPicker()}>
                            <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                        <input
                            ref={pickerRef}
                            type="date"
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                            value={value || ''}
                            min={min}
                            max={max}
                            onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
                        />
                    </InputAdornment>
                )
            }}
        />
    );
};
